import { useRef, useEffect } from 'react';
import type { Node, UpdateQueue, TextNodeData, ImageNodeData, IframeNodeData } from '../../../types';
import { nodeUpdatesToApiPayload, extractNodeId } from '../../../utils/nodeTransformers';
import { logger } from '../../../utils/logger';

interface UseUpdateQueueProps {
  currentSlideId: number | null;
  apiUrl: (path: string) => string;
  setUpdateQueue: (queue: UpdateQueue | ((prev: UpdateQueue) => UpdateQueue)) => void;
}

/**
 * Hook for managing node update queue with debouncing
 *
 * Handles:
 * - Queuing node updates (position, size, zIndex)
 * - Queuing node data updates (content, style, etc.)
 * - Debouncing updates to reduce API calls
 * - Flushing queue to API
 * - Cleanup on unmount
 */
export function useUpdateQueue({
  currentSlideId,
  apiUrl,
  setUpdateQueue,
}: UseUpdateQueueProps) {
  const debounceTimersRef = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const flushUpdateQueue = async () => {
    return new Promise<void>((resolve) => {
      setUpdateQueue(currentQueue => {
        if (Object.keys(currentQueue).length === 0) {
          logger.debug('queue', 'Flush called but queue is empty');
          resolve();
          return currentQueue;
        }

        const queueToFlush = { ...currentQueue };
        const nodeCount = Object.keys(queueToFlush).length;
        logger.info('queue', `Flushing queue with ${nodeCount} node(s)`, queueToFlush);

        Object.keys(debounceTimersRef.current).forEach(key => {
          clearTimeout(debounceTimersRef.current[key]);
          delete debounceTimersRef.current[key];
        });

        const updatePromises = Object.entries(queueToFlush).map(async ([nodeId, updates]) => {
          const payload = nodeUpdatesToApiPayload(updates);

          if (Object.keys(payload).length === 0) {
            logger.debug('queue', `Skipping node ${nodeId} - no changes to send`);
            return;
          }

          try {
            logger.debug('api', `PATCH ${apiUrl(`/slides/${currentSlideId}/nodes/${nodeId}`)}`, payload);
            await fetch(apiUrl(`/slides/${currentSlideId}/nodes/${nodeId}`), {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ node: payload }),
            });
            logger.info('api', `Successfully updated node ${nodeId}`);
          } catch (error) {
            logger.error('api', `Failed to flush updates for node ${nodeId}`, error);
          }
        });

        Promise.all(updatePromises).then(() => {
          logger.info('queue', `Flush complete - sent ${nodeCount} update(s)`);
          resolve();
        });

        return {};
      });
    });
  };

  const queueNodeUpdate = (id: string, updates: Partial<Node>, debounceMs: number) => {
    const nodeId = extractNodeId(id);
    const isFirstUpdate = !debounceTimersRef.current[nodeId];

    setUpdateQueue(prev => {
      const merged = {
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          ...(updates.position && { position: updates.position }),
          ...(updates.size && { size: updates.size }),
          ...(updates.zIndex !== undefined && { zIndex: updates.zIndex }),
        }
      };
      if (isFirstUpdate) {
        logger.debug('queue', `Started queueing updates for node ${nodeId}`);
      }
      return merged;
    });

    if (debounceTimersRef.current[nodeId]) {
      clearTimeout(debounceTimersRef.current[nodeId]);
    }

    debounceTimersRef.current[nodeId] = setTimeout(() => {
      flushUpdateQueue();
      delete debounceTimersRef.current[nodeId];
    }, debounceMs);
  };

  const queueNodeDataUpdate = (
    id: string,
    dataUpdates: Partial<TextNodeData> | Partial<ImageNodeData> | Partial<IframeNodeData>,
    debounceMs: number
  ) => {
    const nodeId = extractNodeId(id);
    const isFirstUpdate = !debounceTimersRef.current[`${nodeId}-data`];

    setUpdateQueue(prev => {
      const merged = {
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          data: {
            ...(prev[nodeId]?.data || {}),
            ...dataUpdates
          }
        }
      };
      if (isFirstUpdate) {
        logger.debug('queue', `Started queueing data updates for node ${nodeId}`);
      }
      return merged;
    });

    if (debounceTimersRef.current[`${nodeId}-data`]) {
      clearTimeout(debounceTimersRef.current[`${nodeId}-data`]);
    }

    debounceTimersRef.current[`${nodeId}-data`] = setTimeout(() => {
      flushUpdateQueue();
      delete debounceTimersRef.current[`${nodeId}-data`];
    }, debounceMs);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.keys(debounceTimersRef.current).forEach(key => {
        clearTimeout(debounceTimersRef.current[key]);
      });
    };
  }, []);

  return {
    flushUpdateQueue,
    queueNodeUpdate,
    queueNodeDataUpdate,
  };
}
